import React, { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useClientTagStore } from '../../lib/stores/clientTagStore';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useApp } from '../AppProvider';
import { MultiChipRow, ValueRow } from '../shared/property-rows';
import { notifyPartnerProfilesChanged } from '../../lib/services/partnerReferrerProfilesService';

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/**
 * Riga "Tag" della scheda partner. I tag stanno sul profilo segnalatore
 * (`referrers.tag_ids`, stesso catalogo di lead e clienti): sono quelli usati dai
 * filtri tag delle liste newsletter "Segnalatori". Si mostra l'unione dei profili
 * del partner e si salva la stessa lista su tutti, così gli eventuali doppioni
 * restano allineati. Senza profilo segnalatore la riga non compare, oppure
 * (`explainMissing`) dice dove crearlo.
 */
export function PartnerTagsRow({ partnerId, explainMissing = false }: { partnerId: string; explainMissing?: boolean }) {
  const { tags: allTags, fetchTags, createTag } = useClientTagStore();
  const { canEditPartner } = usePermissions();
  const { toast } = useApp();
  const [profileIds, setProfileIds] = useState<string[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('referrers')
        .select('id, tag_ids')
        .eq('external_partner_id', partnerId);
      if (cancelled) return;
      if (error) {
        console.error('[PartnerTagsRow] Errore nel caricamento dei tag:', error);
        setProfileIds([]);
        return;
      }
      const rows = (data || []) as { id: string; tag_ids: string[] | null }[];
      setProfileIds(rows.map((r) => r.id));
      setSelected(Array.from(new Set(rows.flatMap((r) => r.tag_ids || []))));
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  if (!profileIds) return null;
  if (profileIds.length === 0) {
    return explainMissing ? (
      <ValueRow icon={rowIcon(Tag)} label="Tag" wrap>
        <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">
          Serve il profilo provvigioni: crealo dalla scheda, sezione Provvigioni
        </span>
      </ValueRow>
    ) : null;
  }

  const save = async (ids: string[]) => {
    const previous = selected;
    setSelected(ids);
    const { error } = await supabase.from('referrers').update({ tag_ids: ids }).in('id', profileIds);
    if (error) {
      setSelected(previous);
      toast.error("Errore durante l'aggiornamento dei tag");
      return;
    }
    notifyPartnerProfilesChanged();
  };

  // Anche i tag assegnati ma disattivati restano leggibili (mai id grezzi)
  const options = allTags.map((t) => ({ value: t.id, label: t.name, color: t.color }));

  if (!canEditPartner) {
    const byId = new Map(options.map((o) => [o.value, o]));
    return (
      <ValueRow icon={rowIcon(Tag)} label="Tag" wrap>
        {selected.length === 0 ? (
          <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">Vuoto</span>
        ) : selected.map((id) => {
          const opt = byId.get(id);
          return (
            <span
              key={id}
              className="inline-flex items-center rounded px-1.5 py-0.5 text-[11.5px] font-medium"
              style={{ backgroundColor: opt?.color ? `${opt.color}20` : 'rgba(55,53,47,0.08)' }}
            >
              <span className="text-[#37352F] dark:text-white">{opt?.label ?? 'Tag'}</span>
            </span>
          );
        })}
      </ValueRow>
    );
  }

  return (
    <MultiChipRow
      icon={rowIcon(Tag)}
      label="Tag"
      selected={selected}
      options={options}
      onAdd={(id) => save([...selected, id])}
      onRemove={(id) => save(selected.filter((x) => x !== id))}
      onCreate={async (name) => {
        const created = await createTag({ name, color: '#E91E8C' });
        if (created?.id) await save([...selected, created.id]);
        else toast.error('Errore nella creazione del tag');
      }}
      addPlaceholder="Aggiungi tag..."
    />
  );
}
